import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api } from "../../lib/api";
import { AdminLayout } from "../../components/admin/AdminLayout";
import { StatusBadge } from "../../components/StatusBadge";
import { ClipboardList, Users, Package, Truck, AlertTriangle, MessageSquare } from "lucide-react";

export default function AdminDashboard() {
  const navigate = useNavigate();
  useEffect(() => { document.title = "Dashboard | Sambiva Admin"; }, []);
  const { data } = useQuery({ queryKey: ["admin-dashboard"], queryFn: async () => (await api.get("/admin/dashboard")).data });

  const kpis = [
    { label: "Pending Applications", value: data?.pending_applications, icon: ClipboardList, to: "/admin/applications", color: "text-sambiva-red bg-sambiva-red/10" },
    { label: "Approved Customers", value: data?.approved_customers, icon: Users, to: "/admin/customers", color: "text-sambiva-green bg-sambiva-green/10" },
    { label: "Orders Received", value: data?.orders_received, icon: Package, to: "/admin/orders", color: "text-blue-600 bg-blue-100" },
    { label: "Orders On Route", value: data?.orders_on_route, icon: Truck, to: "/admin/orders", color: "text-[#8a6d00] bg-sambiva-yellow/20" },
    { label: "Low Stock Products", value: data?.low_stock_count, icon: AlertTriangle, to: "/admin/catalogue", color: "text-orange-600 bg-orange-100" },
    { label: "New Enquiries", value: data?.new_enquiries, icon: MessageSquare, to: "/admin/enquiries", color: "text-purple-600 bg-purple-100" },
  ];

  return (
    <AdminLayout title="Dashboard">
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
        {kpis.map((k) => (
          <button key={k.label} data-testid={`kpi-${k.label.toLowerCase().replace(/\s+/g, "-")}`} onClick={() => navigate(k.to)} className="bg-white rounded-2xl p-5 border border-black/5 text-left hover:shadow-md transition-shadow">
            <div className={`w-10 h-10 rounded-xl flex items-center justify-center mb-3 ${k.color}`}><k.icon size={20} /></div>
            <p className="text-2xl font-bold text-sambiva-charcoal">{k.value ?? "—"}</p>
            <p className="text-xs text-sambiva-muted mt-1">{k.label}</p>
          </button>
        ))}
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <Panel title="Recent Orders" onAll={() => navigate("/admin/orders")}>
          {(data?.recent_orders || []).map((o) => (
            <div key={o.id} onClick={() => navigate(`/admin/orders/${o.id}`)} className="flex items-center justify-between py-2.5 border-b border-black/5 last:border-0 cursor-pointer hover:bg-sambiva-cream/40 px-2 -mx-2 rounded">
              <div><p className="font-medium text-sm text-sambiva-charcoal">{o.order_number}</p><p className="text-xs text-sambiva-muted">{o.company_name}</p></div>
              <div className="text-right"><p className="text-sm font-medium">€{o.total.toFixed(2)}</p><StatusBadge status={o.status} /></div>
            </div>
          ))}
          {(data?.recent_orders || []).length === 0 && <p className="text-sm text-sambiva-muted py-4">No orders yet.</p>}
        </Panel>

        <Panel title="Pending Partners" onAll={() => navigate("/admin/applications")}>
          {(data?.pending_partners || []).map((c) => (
            <div key={c.id} onClick={() => navigate(`/admin/customers/${c.id}`)} className="flex items-center justify-between py-2.5 border-b border-black/5 last:border-0 cursor-pointer hover:bg-sambiva-cream/40 px-2 -mx-2 rounded">
              <div><p className="font-medium text-sm text-sambiva-charcoal">{c.company_name}</p><p className="text-xs text-sambiva-muted">{c.country} · {c.business_type}</p></div>
              <StatusBadge status={c.status} />
            </div>
          ))}
          {(data?.pending_partners || []).length === 0 && <p className="text-sm text-sambiva-muted py-4">No pending partners.</p>}
        </Panel>

        <Panel title="Low Stock Products" onAll={() => navigate("/admin/catalogue")}>
          {(data?.low_stock_products || []).map((p) => (
            <div key={p.id} className="flex items-center justify-between py-2.5 border-b border-black/5 last:border-0">
              <div className="flex items-center gap-2"><img src={p.images?.[0]} alt="" className="w-8 h-8 rounded object-cover" /><p className="font-medium text-sm text-sambiva-charcoal">{p.name}</p></div>
              <span className="text-sm font-semibold text-orange-600">{p.stock_quantity} left</span>
            </div>
          ))}
          {(data?.low_stock_products || []).length === 0 && <p className="text-sm text-sambiva-muted py-4">All products well stocked.</p>}
        </Panel>
      </div>
    </AdminLayout>
  );
}

function Panel({ title, children, onAll }) {
  return (
    <div className="bg-white rounded-2xl border border-black/5 p-5">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-semibold text-sambiva-charcoal">{title}</h3>
        <button onClick={onAll} className="text-xs text-sambiva-green font-semibold hover:underline">View all</button>
      </div>
      {children}
    </div>
  );
}
